import React, { memo, useMemo } from 'react';
import { Stack } from '@mui/material';
import type { WorkflowNode } from '../../../types/backend';
import WorkflowStepCard from './WorkflowStepCard';
import WorkflowStageFrame from './WorkflowStageFrame';
import { stageSkipped, stageSummary, stepStatus, toStages } from '../utils/workflowStages';
import type { StagesRun } from '../utils/workflowStages';

export interface WorkflowStagesProps {
  nodes: WorkflowNode[];
  /** A run walking these nodes. Omitted, the view shows the procedure with no progress. */
  run?: StagesRun;
}

// A workflow is stages, not a graph (ADR-0055): a layer is a barrier and its members are
// unordered, so a stacked list says exactly that, where an edge per pair of steps in adjacent
// layers drew crossings that carried no information.
const WorkflowStages = memo<WorkflowStagesProps>(({ nodes, run }) => {
  // Memoised because the parent modal re-renders on every gate answer while the plan is fixed.
  const stages = useMemo(() => toStages(nodes), [nodes]);
  // Memoised with the stages: every step card resolves its depends_on ids against this.
  const labels = useMemo(() => new Map(nodes.map((n) => [n.id, n.label])), [nodes]);

  return (
    <Stack component="ol" spacing={0} sx={{ listStyle: 'none', m: 0, p: 0, minWidth: 0 }}>
      {stages.map((stage, i) => {
        const current = run !== undefined && run.current_layer === stage.layer;
        const skipped = run !== undefined && stageSkipped(stage, run);
        return (
          <WorkflowStageFrame
            key={stage.layer}
            layer={stage.layer}
            first={i === 0}
            summary={stageSummary(stage.nodes)}
            current={current}
            skipped={skipped}
          >
            {stage.nodes.map((node) => (
              <WorkflowStepCard
                key={node.id}
                node={node}
                labels={labels}
                run={run}
                status={run ? stepStatus(node, run) : undefined}
              />
            ))}
          </WorkflowStageFrame>
        );
      })}
    </Stack>
  );
});

WorkflowStages.displayName = 'WorkflowStages';

export default WorkflowStages;
